import { forwardRef } from "react";

import { cn } from "../lib/cn";

export type ButtonVariant = "gold" | "outline" | "nav" | "ghost" | "link";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-5 py-2 min-h-[36px] text-[9px]",
  md: "px-[38px] py-[15px] min-h-[48px] text-[10.5px]",
  lg: "px-12 py-5 min-h-[56px] text-[11px]",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "gold",
      size = "md",
      loading,
      disabled,
      className,
      children,
      ...props
    },
    ref,
  ) {
    const isDisabled = disabled ?? loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          "inline-flex items-center justify-center font-mono uppercase tracking-[0.15em] transition-all duration-300",
          variant !== "link" && "octa-sm",
          variant !== "link" &&
            "[filter:drop-shadow(4px_4px_0_rgba(0,0,0,0.45))] hover:[filter:drop-shadow(6px_6px_0_rgba(0,0,0,0.50))]",
          "hover:-translate-y-0.5 active:translate-y-0",
          "disabled:pointer-events-none disabled:opacity-35",
          variant === "gold" && [
            "bg-gold font-medium text-black",
            "hover:opacity-92 hover:shadow-[0_0_24px_rgba(201,168,76,0.15)]",
            sizeClasses[size],
          ],
          variant === "outline" && [
            "text-gold bg-[rgba(201,168,76,0.08)] backdrop-blur-sm",
            "hover:bg-[rgba(201,168,76,0.12)]",
            sizeClasses[size],
          ],
          variant === "nav" && [
            "text-text-dim bg-transparent px-[22px] py-[9px] text-[10.5px]",
            "hover:bg-gold hover:text-black",
          ],
          variant === "ghost" && [
            "text-text-dim bg-transparent",
            "hover:bg-gold-glow hover:text-gold",
            sizeClasses[size],
          ],
          variant === "link" && [
            "text-gold gap-2 bg-transparent p-0 text-[10.5px]",
            "hover:gap-3",
          ],
          className,
        )}
        {...props}
      >
        {loading ? (
          <span
            className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
            role="status"
          />
        ) : (
          children
        )}
        {variant === "link" && !loading && (
          <span aria-hidden="true">&rarr;</span>
        )}
      </button>
    );
  },
);
